import * as cn from "classnames";
import * as d3 from "d3";
import { ScaleBand, ScaleLinear } from "d3";
import * as React from "react";
import { AutoScaleSvg } from "..";
import { SpiderWebChartData } from "./api";
import "./SpiderWebChart.css";
import { formatNumber } from "@common";

export interface SpiderWebChartProps {
    color: string;
    data: SpiderWebChartData;
    totalAmount?: number;
    toFixed?: number;
    showAsPercent?: boolean;
}

export function SpiderWebChart({ data, color, showAsPercent, toFixed }: SpiderWebChartProps) {
    const fillColor = d3.color(color);
    fillColor.opacity = 0.3;

    const dayOfWeekScale = d3
        .scaleBand()
        .domain(daysOfWeek.map(v => v.dimension))
        .range([0, degToRad(360)]);

    const valueScale = d3
        .scaleLinear()
        .domain([0, d3.max(data, v => v.value)])
        .range([0, CHART_RADIUS - 5])
        .nice(5);
    return (
        <AutoScaleSvg viewportWidth={CHART_SIZE} viewportHeight={CHART_SIZE} className="SpiderWebChart">
            {renderAxis(dayOfWeekScale, valueScale)}
            {renderMetric(data, dayOfWeekScale, valueScale, fillColor.toString(), color, toFixed, showAsPercent)}
        </AutoScaleSvg>
    );
}

function renderAxis(dayOfWeekScale: ScaleBand<string>, valueScale: ScaleLinear<number, number>) {
    const line = d3
        .line<Point>()
        .x(p => p.x)
        .y(p => p.y);

    const axisEndUnrotated: Point = {
        x: CHART_CENTER_POINT.x,
        y: CHART_CENTER_POINT.y - CHART_RADIUS - 20
    };

    const textPoint: Point = {
        x: axisEndUnrotated.x,
        y: axisEndUnrotated.y - 5
    };

    const radialAxisData = daysOfWeek.map(v => {
        const angle = dayOfWeekScale(v.dimension);

        return {
            endPoint: rotateAroundCenter(axisEndUnrotated, CHART_CENTER_POINT, angle),
            textEndPoint: rotateAroundCenter(textPoint, CHART_CENTER_POINT, angle),
            dayOfWeek: v
        };
    });

    // Array of data required for drawing circlular axis around center at specified value
    const circularAxisData = (value: number) => {
        const points = daysOfWeek.map(dow =>
            rotateAroundCenter(
                {
                    x: CHART_CENTER_POINT.x,
                    y: CHART_CENTER_POINT.y - valueScale(value)
                },
                CHART_CENTER_POINT,
                dayOfWeekScale(dow.dimension)
            )
        );

        return [...points, points[0]];
    };

    return (
        <g width={CHART_SIZE} height={CHART_SIZE} className="axis">
            {radialAxisData.map(item => {
                return (
                    <React.Fragment key={item.dayOfWeek.dimension}>
                        <path
                            className="report-axis"
                            d={line([CHART_CENTER_POINT, item.endPoint])}
                            vectorEffect="non-scaling-stroke"
                        />
                        <text
                            x={item.textEndPoint.x}
                            y={item.textEndPoint.y}
                            className={cn("report-axis-label", "day-of-week", item.dayOfWeek.dimension.toLowerCase())}
                        >
                            {item.dayOfWeek.name}
                        </text>
                    </React.Fragment>
                );
            })}
            {valueScale
                .ticks(5)
                .map((value, index) => (
                    <path
                        className="report-axis"
                        d={line(circularAxisData(value))}
                        vectorEffect="non-scaling-stroke"
                        key={`RadialAxis-${index}`}
                    />
                ))}
        </g>
    );
}

function renderMetric(
    data: SpiderWebChartData,
    dayOfWeekScale: ScaleBand<string>,
    valueScale: ScaleLinear<number, number>,
    fillColor: string,
    strokeColor: string,
    toFixed: number = 2,
    showAsPercent: boolean = false
) {
    const dataWithValues = daysOfWeek
        .map(day => ({
            dayOfWeek: day,
            data: data.find(i => i.day.toLowerCase() === day.dimension.toLowerCase())
        }))
        .map(i => ({
            ...i,
            value: i.data ? i.data.value : 0
        }));

    let coords = dataWithValues.map(i => {
        const linePoint = {
            x: CHART_CENTER_POINT.x,
            y: CHART_CENTER_POINT.y - valueScale(i.value)
        };

        const angle = dayOfWeekScale(i.dayOfWeek.dimension);

        return rotateAroundCenter(linePoint, CHART_CENTER_POINT, angle);
    });

    coords = [...coords, coords[0]]; // Close the line

    const line = d3
        .line<Point>()
        .x(d => d.x)
        .y(d => d.y);

    return (
        <g className="metric">
            <path d={line(coords)} fill={fillColor} stroke={strokeColor} strokeWidth={1.5} />
            {dataWithValues.map(i => {
                const textOriginPoint = {
                    x: CHART_CENTER_POINT.x,
                    y: CHART_CENTER_POINT.y - valueScale(i.value) - 10
                };

                const angle = dayOfWeekScale(i.dayOfWeek.dimension);

                const textPoint = rotateAroundCenter(textOriginPoint, CHART_CENTER_POINT, angle);

                return (
                    <text
                        className={cn("report-data-value-label", "day-of-week", i.dayOfWeek.dimension.toLowerCase())}
                        x={textPoint.x}
                        y={textPoint.y}
                        key={`MetricValue-${i.dayOfWeek.dimension}`}
                    >
                        {showAsPercent ? `${(i.value * 100).toFixed(toFixed)}%` : formatNumber(i.value)}
                    </text>
                );
            })}
        </g>
    );
}

const CHART_RADIUS = 80;
const CHART_SIZE = 275;

const CHART_CENTER_POINT: Point = {
    x: CHART_SIZE / 2,
    y: CHART_SIZE / 2
};

const daysOfWeek = [
    { dimension: "Mon", name: "Monday" },
    { dimension: "Tue", name: "Tuesday" },
    { dimension: "Wed", name: "Wednesday" },
    { dimension: "Thu", name: "Thursday" },
    { dimension: "Fri", name: "Friday" },
    { dimension: "Sat", name: "Saturday" },
    { dimension: "Sun", name: "Sunday" }
];

const degToRad = (deg: number) => (deg * Math.PI) / 180;

interface Point {
    x: number;
    y: number;
}

function rotateAroundCenter(point: Point, center: Point, angleRad: number): Point {
    const originPoint = {
        x: point.x - center.x,
        y: point.y - center.y
    };

    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    const rotatedOrigin = {
        x: originPoint.x * cos - originPoint.y * sin,
        y: originPoint.x * sin + originPoint.y * cos
    };

    return {
        x: rotatedOrigin.x + center.x,
        y: rotatedOrigin.y + center.y
    };
}
